import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import EChartCustomized, { SCHEMES } from '@/components/EchartCustomized'
import type { EChartsOption } from 'echarts'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { getUserFlow } from '../../services/analytics'
import { UserFlow } from '../../types/analytics'
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@/components/ui/card'
import { GitBranch } from 'lucide-react'

interface FlowSectionProps {
    from: string
    to: string
}

const FlowSection: React.FC<FlowSectionProps> = ({ from, to }) => {
    const { t } = useTranslation()

    const { data, renderState } = useQueryWithHandler<UserFlow>({
        queryKey: [QUERY_KEYS.LIVECHAT_ANALYTICS_FLOW, from, to],
        queryFn: () => getUserFlow({ from, to }),
    })

    const chartOptions: EChartsOption = useMemo(() => {
        if (!data || data.nodes.length === 0) return {}

        return {
            tooltip: {
                trigger: 'item',
                triggerOn: 'mousemove',
                formatter: (params: {
                    dataType: string
                    data: { source?: string; target?: string; value: number }
                    name?: string
                    value?: number
                }) => {
                    // Helper to convert path to readable label
                    const toLabel = (path: string) =>
                        path === '/'
                            ? 'Home'
                            : path.replace(/^\//, '').replace(/-/g, ' ')

                    if (params.dataType === 'edge') {
                        const { source, target, value } = params.data
                        return `${toLabel(source || '')} → ${toLabel(target || '')}<br/>${t('Visitors')}: <b>${value}</b>`
                    }
                    return `${toLabel(params.name || '')}<br/>${t('Visitors')}: <b>${params.value}</b>`
                },
            },
            series: [
                {
                    type: 'sankey',
                    emphasis: {
                        focus: 'adjacency',
                    },
                    nodeAlign: 'justify',
                    nodeGap: 12,
                    nodeWidth: 20,
                    layoutIterations: 32,
                    data: data.nodes.map((node, index) => ({
                        // Use node.id as name since links reference by id (path)
                        name: node.id,
                        value: node.value,
                        itemStyle: {
                            color: SCHEMES.vcloud[
                                index % SCHEMES.vcloud.length
                            ],
                        },
                    })),
                    links: data.links.map((link) => ({
                        source: link.source,
                        target: link.target,
                        value: link.value,
                    })),
                    lineStyle: {
                        color: 'gradient',
                        curveness: 0.5,
                        opacity: 0.4,
                    },
                    label: {
                        show: true,
                        position: 'right',
                        color: SCHEMES.black,
                        fontSize: 11,
                        formatter: (params: { name: string }) => {
                            // Convert path to readable label
                            const path = params.name
                            const label =
                                path === '/'
                                    ? 'Home'
                                    : path.replace(/^\//, '').replace(/-/g, ' ')
                            // Truncate long names
                            if (label.length > 30) {
                                return label.substring(0, 27) + '...'
                            }
                            return label
                        },
                    },
                },
            ],
        }
    }, [data, t])

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    if (!data || data.nodes.length === 0) {
        return (
            <Card>
                <CardHeader>
                    <CardTitle className="text-base">
                        {t('User Navigation Flow')}
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="text-center py-12 text-muted-foreground">
                        <GitBranch className="mx-auto h-12 w-12 mb-4 opacity-50" />
                        <p className="text-sm">
                            {t(
                                'No user flow data available for the selected period'
                            )}
                        </p>
                        <p className="text-xs mt-1">
                            {t('User flow data requires page visit tracking')}
                        </p>
                    </div>
                </CardContent>
            </Card>
        )
    }

    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-base">
                    {t('User Navigation Flow')}
                </CardTitle>
                <CardDescription>
                    {t(
                        'Showing how users navigate through pages before starting a chat'
                    )}
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                <EChartCustomized
                    options={chartOptions}
                    style={{ height: 500, width: '100%' }}
                    opts={{ renderer: 'svg' }}
                />

                <div className="flex gap-4 text-xs text-muted-foreground">
                    <div className="flex items-center gap-2">
                        <div
                            className="w-3 h-3 rounded-sm"
                            style={{ backgroundColor: SCHEMES.vcloud[0] }}
                        />
                        <span>{t('Entry pages')}</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div
                            className="w-3 h-3 rounded-sm"
                            style={{ backgroundColor: SCHEMES.vcloud[2] }}
                        />
                        <span>{t('Navigation path')}</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div
                            className="w-3 h-3 rounded-sm"
                            style={{ backgroundColor: SCHEMES.vcloud[4] }}
                        />
                        <span>{t('Chat initiation')}</span>
                    </div>
                </div>
            </CardContent>
        </Card>
    )
}

export default FlowSection
